import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button, Table, Tag, Empty, Spin, Modal, Divider,
  Typography, Row, Col, Space, Select, Dropdown, message
} from 'antd';
import {
  ArrowLeftOutlined, FileTextOutlined, DownloadOutlined, FilePdfOutlined, FileExcelOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { subscriptionActions } from '../../actions/subscriptionActions';
import logo from '../../../public/assets/logo.png';

const { Text } = Typography;

const DriveMyntLogo: React.FC<{ size?: number }> = ({ size = 36 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <img
      src={logo}
      alt="DrivMynt Logo"
      style={{ width: size, height: size, objectFit: 'contain' }}
      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
    />
    <span style={{ fontSize: size * 0.55, fontWeight: 800, letterSpacing: -0.5, lineHeight: 1 }}>
      <span style={{ color: '#111827' }}>Drive</span>
      <span style={{ color: '#1890ff' }}>Mynt</span>
    </span>
  </div>
);

const InvoiceDetailModal: React.FC<{
  open: boolean;
  onClose: () => void;
  invoice: any;
  tenantName?: string;
  tenantEmail?: string;
  tenantAddress?: string;
  isLoading?: boolean;
}> = ({ open, onClose, invoice, tenantName, tenantEmail, tenantAddress, isLoading }) => {

  const dispatch = useDispatch();
  const getInvoicePDFReducer = useSelector((state: any) => state.getInvoicePDFReducer);
 

  const handleDownload = () => {
    dispatch(subscriptionActions.getInvoicePDFRequest({ invoiceId: invoice.transactionId }));
  };

  const GST_PERCENTAGE = 18;

  const itemColumns: ColumnsType<any> = [
    {
      title: 'Description',
      key: 'description',
      render: (_: any, r: any) => {
        if (r.upgradeDetails?.isUpgrade) {
          return (
            <div>
              <Text strong>
                {r.plan ? `${r.plan.name} - ${r.plan.billingCycle} Subscription (Plan Upgrade)` : 'Plan Upgrade'}
              </Text>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                Upgraded from {r.upgradeDetails.previousPlanName} (Credit applied: ₹{(r.upgradeDetails.unusedCredit ?? 0).toFixed(2)})
              </div>
            </div>
          );
        }
        return r.plan ? `${r.plan.name} - ${r.plan.billingCycle} Subscription` : 'Subscription';
      },
    },
    {
      title: 'Qty',
      key: 'qty',
      align: 'center' as const,
      render: () => 1,
    },
    {
      title: 'Taxable Value',
      key: 'taxableValue',
      align: 'right' as const,
      render: (_: any, r: any) => `₹${(r.pricing?.taxableValue ?? 0).toFixed(2)}`,
    },
    {
      title: `GST (${invoice?.pricing?.gstPercentage ?? 18}%)`,
      key: 'gst',
      align: 'right' as const,
      render: (_: any, r: any) => `₹${(r.pricing?.gstAmount ?? 0).toFixed(2)}`,
    },
    {
      title: 'Total',
      key: 'total',
      align: 'right' as const,
      render: (_: any, r: any) => (
        <Text strong>₹{(r.pricing?.totalAmount ?? 0).toFixed(2)}</Text>
      ),
    },
  ];

  const recipientName = invoice?.user?.name || tenantName || 'N/A';
  const recipientEmail = invoice?.user?.email || tenantEmail;
  const recipientAddress = tenantAddress;

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={980} title={null} className="ph-invoice-modal">
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}><Spin size="large" /></div>
      ) : !invoice ? (
        <Empty description="Invoice not found" />
      ) : (
        <div className="ph-invoice">
          <div className="ph-invoice-header">

            <div className="ph-invoice-logo-wrap" style={{ marginBottom: 16 }}>
              <DriveMyntLogo size={80} />
            </div>

            <div className="ph-invoice-meta" >
              <div className="ph-invoice-date">
                Invoice Date:{' '}
                {invoice.paidAt ? dayjs(invoice.paidAt).format('DD MMM YYYY') : '-'}
              </div>
            </div>
          </div>

          <Divider style={{ margin: '16px 0' }} />

          <Row gutter={24} className="ph-invoice-parties">
            <Col xs={24} sm={12}>
              <div className="ph-invoice-party-box">
                <div className="ph-invoice-party-label">Supplier Details</div>
                <div className="ph-invoice-party-name">Auxwit Technologies Pvt. Ltd.</div>
                <div className="ph-invoice-party-detail"> Gowriwakkam,Chennai-600073</div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="ph-invoice-party-box ph-invoice-party-box--right">
                <div className="ph-invoice-party-label">Recipient Details</div>
                <div className="ph-invoice-party-name">{recipientName}</div>
                {recipientEmail && <div className="ph-invoice-party-detail">{recipientEmail}</div>}
                {recipientAddress && <div className="ph-invoice-party-detail">{recipientAddress}</div>}
              </div>
            </Col>
          </Row>

          <Divider style={{ margin: '16px 0' }} />

          {/* Upgrade Breakdown Highlight */}
          {invoice.upgradeDetails?.isUpgrade && (
            <div
              style={{
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: 8,
                padding: '12px 16px',
                marginBottom: 16
              }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Upgraded Plan</Text>
                  <Text strong>{invoice.plan?.name || 'Upgraded Plan'}</Text>
                </Col>
                <Col xs={24} sm={8}>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Previous Plan</Text>
                  <Text strong>
                    {invoice.upgradeDetails.previousPlanName}
                    {invoice.upgradeDetails.previousPlanFullPrice != null
                      ? ` (₹${Number(invoice.upgradeDetails.previousPlanFullPrice).toFixed(2)})`
                      : ''}
                  </Text>
                </Col>
                <Col xs={24} sm={8}>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Remaining / Unused Credit</Text>
                  <Text strong style={{ color: '#52c41a' }}>
                    -₹{Number(invoice.upgradeDetails.unusedCredit ?? 0).toFixed(2)}
                    {invoice.upgradeDetails.proratedDays ? ` (${invoice.upgradeDetails.proratedDays} days remaining)` : ''}
                  </Text>
                </Col>
              </Row>
            </div>
          )}

          {/* Table */}
          <Table
            columns={itemColumns}
            dataSource={[invoice]}
            pagination={false}
            rowKey="transactionId"
            size="small"
            className="ph-invoice-table"
            summary={() => {
              const isUpgrade = Boolean(invoice.upgradeDetails?.isUpgrade);
              return (
                <>
                  {isUpgrade && (
                    <>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={4} align="right">
                          <Text type="secondary">Upgraded Plan Cost</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <Text>
                            ₹{(invoice.upgradeDetails.newPlanCost ?? invoice.plan?.price ?? 0).toFixed(2)}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={4} align="right">
                          <Text style={{ color: '#52c41a' }}>
                            Less: Unused Credit ({invoice.upgradeDetails.previousPlanName})
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <Text style={{ color: '#52c41a' }}>
                            -₹{(invoice.upgradeDetails.unusedCredit ?? 0).toFixed(2)}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </>
                  )}
                  <Table.Summary.Row className="ph-invoice-total-row">
                    <Table.Summary.Cell index={0} colSpan={4} align="right">
                      <Text strong>{isUpgrade ? 'Final Payable (Grand Total)' : 'Grand Total'}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong style={{ fontSize: 15 }}>
                        ₹{(invoice.pricing?.totalAmount ?? 0).toFixed(2)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              );
            }}
          />

          {/* Footer */}
          <div className="ph-invoice-footer-row" style={{ marginTop: 24 }}>
            <div className="ph-invoice-footer-sig" />
            <div className="ph-invoice-footer-right" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
                This is a system generated invoice and does not require a signature.
              </Text>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                style={{ marginLeft: 12 }}
                size="small"
                loading={Boolean(getInvoicePDFReducer?.isLoading)}
              >
                Download Invoice
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

const PurchaseHistory: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Determine role from stored auth data
  const authData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('AUTHDATA') || '{}');
    } catch {
      return {};
    }
  }, []);

  const isSuperAdmin = authData?.role === 'SUPER_ADMIN';

  // Admin: use own tenant (from auth). Super Admin: overall view across all schools.
  const tenantId = isSuperAdmin ? undefined : (location.state?.tenantId || authData?.tenantId);
  const tenantName = location.state?.tenantName;
  const tenantEmail = location.state?.tenantEmail;
  const tenantAddress = location.state?.tenantAddress;

  const isOverall = isSuperAdmin;

  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 10;

  // Filter state
  const currentYear = dayjs().year();
  const [filterType, setFilterType] = useState<'year' | 'month'>('month');
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(dayjs().month() + 1);

  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);
  const monthOptions = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
  ];

  const allInvoicesState = useSelector((state: any) => state.getAllInvoicesReducer);
  const singleInvoiceState = useSelector((state: any) => state.getInvoiceReducer);

   console.log("called:", allInvoicesState?.data?.invoices)

  const isLoading = Boolean(allInvoicesState?.isLoading);
  const isInvoiceLoading = Boolean(singleInvoiceState?.isLoading);
  const invoices: any[] = allInvoicesState?.data?.invoices ?? [];
  const total: number = allInvoicesState?.data?.pagination?.total ?? 0;
  const singleInvoice = singleInvoiceState?.data ?? null;

  useEffect(() => {
    if (isOverall || tenantId) {
      const payload: any = { page, limit, filterType, year: selectedYear };
      if (filterType === 'month') {
        payload.month = selectedMonth;
      }
      if (isOverall) {
        payload.isOverall = true;
      } else {
        payload.tenantId = tenantId;
      }
      dispatch(subscriptionActions.getAllInvoicesRequest(payload));
    }
  }, [dispatch, tenantId, isOverall, page, filterType, selectedYear, selectedMonth]);

  const handleFilterTypeChange = (value: 'year' | 'month') => {
    setFilterType(value);
    setPage(1);
  };

  const handleYearChange = (value: number) => {
    setSelectedYear(value);
    setPage(1);
  };

  const handleMonthChange = (value: number) => {
    setSelectedMonth(value);
    setPage(1);
  };

  const handleViewInvoice = (record: any) => {
    dispatch(subscriptionActions.getInvoiceRequest(record.transactionId));
    setInvoiceModalOpen(true);
  };

  const goBack = () => {
    if (isOverall) {
      navigate('/tenants');
    } else {
      navigate('/tenants/view', { state: { tenantId } });
    }
  };

  const renderInvoiceDate = (r: any) => {
    if (!r.paidAt) return '-';
    return filterType === 'month'
      ? dayjs(r.paidAt).format('MMM DD, YYYY')
      : dayjs(r.paidAt).format('MMM YYYY');
  };

  const columns: ColumnsType<any> = [
    ...(isOverall
      ? [
        {
          title: 'School Name',
          key: 'schoolName',
          render: (_: any, r: any) => (
            <Typography.Text strong>
              {r.tenant?.tenantName || r.tenantName || '-'}
            </Typography.Text>
          ),
        },
      ]
      : []),
    {
      title: 'Subscription / Plan Name',
      key: 'planName',
      render: (_: any, r: any) => (
        <Space direction="vertical" size={2}>
          <Space>
            <div className="ph-plan-icon"><FileTextOutlined /></div>
            <Typography.Text strong>{r.plan?.name || '-'}</Typography.Text>
            {r.upgradeDetails?.isUpgrade && <Tag color="blue">Upgrade</Tag>}
          </Space>
          {r.upgradeDetails?.isUpgrade && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              Credit: -₹{(r.upgradeDetails.unusedCredit ?? 0).toFixed(2)} ({r.upgradeDetails.previousPlanName})
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Invoice Date',
      key: 'invoiceDate',
      render: (_: any, r: any) => renderInvoiceDate(r),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_: any, r: any) => <Typography.Text strong>₹{(r.pricing?.totalAmount ?? 0).toFixed(2)}</Typography.Text>,
    },
    {
      title: 'Payment Method',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (m: string) => m ? <Tag color="blue">{m}</Tag> : <Tag>-</Tag>,
    },
    {
      title: 'Action',
      key: 'action',
      align: 'right' as const,
      render: (_: any, record: any) => (
        <Button size="small" icon={<FileTextOutlined />} onClick={() => handleViewInvoice(record)}>
          Invoice
        </Button>
      ),
    },
  ];

  const exportState = useSelector((state: any) => state.exportInvoicesReducer);
  const [exportingType, setExportingType] = useState<'pdf' | 'excel' | null>(null);

  const buildFilterPayload = () => {
    const payload: any = { filterType, year: selectedYear };
    if (filterType === 'month') {
      payload.month = selectedMonth;
    }
    if (isOverall) {
      payload.isOverall = true;
    } else {
      payload.tenantId = tenantId;
    }
    return payload;
  };

  const handleExport = (format: 'pdf' | 'excel') => {
    setExportingType(format);
    dispatch(subscriptionActions.getOverallInvoicesRequest({
      ...buildFilterPayload(),
      format,
    }));
  };

  useEffect(() => {
    if (exportState?.isSuccess && exportState?.data) {
      const blob = new Blob(
        [Uint8Array.from(atob(exportState.data), (c) => c.charCodeAt(0))],
        { type: exportingType === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Purchase-History.${exportingType === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      setExportingType(null);
      message.success('Export downloaded successfully');
    } else if (exportState?.isFailure) {
      message.error(exportState?.message || 'Export failed');
      setExportingType(null);
    }
  }, [exportState?.isSuccess, exportState?.isFailure]);

  const exportMenuItems = [
    { key: 'pdf', label: 'Export as PDF', icon: <FilePdfOutlined /> },
    { key: 'excel', label: 'Export as Excel', icon: <FileExcelOutlined /> },
  ];



  if (!isOverall && !tenantId) {
    return (
      <div className="batch-view-page">
        <div className="batch-view-header">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/tenants')} className="batch-back-btn">Tenants</Button>
        </div>
        <Empty description="No tenant context found" style={{ marginTop: 40 }} />
      </div>
    );
  }

  return (
    <div className="batch-view-page ph2-page">
      {/* Header */}
      <div className="batch-view-header">
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={goBack} className="batch-back-btn">
          {isOverall ? 'Tenants' : (tenantName || 'Tenant')}
        </Button>
      </div>

      {/* Page title + Filters */}
      <div className="bh2-page-header" style={{ alignItems: 'center' }}>
        <div className="bh2-page-icon"><FileTextOutlined /></div>
        <div style={{ flex: 1 }}>
          <h2 className="bh2-page-title" style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {isOverall ? 'Overall Purchase History' : 'Purchase History'}
          </h2>
          <Typography.Text type="secondary" className="bh2-page-sub">
            {isOverall
              ? 'All invoices and payment records across all schools'
              : 'All invoices and payment records for this tenant'}
          </Typography.Text>
        </div>

        <Space>
          <Select
            value={filterType}
            onChange={handleFilterTypeChange}
            style={{ width: 110 }}
            options={[
              { value: 'month', label: 'Month' },
              { value: 'year', label: 'Year' },
            ]}
          />
          <Select
            value={selectedYear}
            onChange={handleYearChange}
            style={{ width: 100 }}
            options={yearOptions.map((y) => ({ value: y, label: String(y) }))}
          />
          {filterType === 'month' && (
            <Select
              value={selectedMonth}
              onChange={handleMonthChange}
              style={{ width: 130 }}
              options={monthOptions}
            />
          )}
          {<Dropdown
            menu={{
              items: exportMenuItems,
              onClick: ({ key }) => handleExport(key as 'pdf' | 'excel'),
            }}
            trigger={['click']}
            disabled={allInvoicesState?.data?.invoices.length === 0 ? true : false}
          >
            <Button
              type="default"
              icon={<DownloadOutlined />}
              loading={Boolean(exportState?.isLoading)}
            >
              Export
            </Button>
          </Dropdown>}
        </Space>
      </div>

      {/* Table */}
      <div className="ph2-table-card">
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}><Spin size="large" /></div>
        ) : (
          <Table
            columns={columns}
            dataSource={invoices}
            rowKey="transactionId"
            loading={isLoading}
            className="ph-invoice-list-table"
            pagination={
              total > limit
                ? {
                  current: page,
                  pageSize: limit,
                  total,
                  showSizeChanger: false,
                  showTotal: (t) => `${t} invoice${t !== 1 ? 's' : ''}`,
                  onChange: (p) => setPage(p),
                }
                : false
            }
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No invoices found"
                />
              ),
            }}
          />
        )}
      </div>

      {/* Invoice Detail Modal — keep existing unchanged */}
      <InvoiceDetailModal
        open={invoiceModalOpen}
        onClose={() => setInvoiceModalOpen(false)}
        invoice={singleInvoice}
        tenantName={isOverall ? (singleInvoice?.tenant?.tenantName || singleInvoice?.tenantName) : tenantName}
        tenantEmail={tenantEmail}
        tenantAddress={tenantAddress}
        isLoading={isInvoiceLoading}
      />
    </div>
  );
};

export default PurchaseHistory;